import { EMAIL_FIELD_NAME, EmailValidationWrapperPropsType } from "./type";
import React from "react";
import FieldAlias from "shared/form/renderer/enums/FieldAlias";
import { SchemaField } from "shared/form/renderer/types/SchemaField";
import * as yup from "yup";
import Validation from "shared/form/Validation";
import { useCopies } from "shared/hooks/useCopies";
import { useValidateEmailDomain } from "shared/dataloader";
import { updateWorkEmailAfterDomainValidation } from "../../dataloader";
import { useQueryClient } from "react-query";
import { RegistrationStep } from "shared/utils/enums";

export default function useEmailValidationWrapperService({
  setCurrentStep,
  ...props
}: EmailValidationWrapperPropsType) {
  const copies = useCopies();
  const queryClient = useQueryClient();
  const formRef = React.useRef<any>();
  const [domainValidationResult, setDomainValidationResult] = React.useState<
    { email: string; valid: boolean } | undefined
  >();
  const { mutateAsync: validateEmailDomain } = useValidateEmailDomain();
  const workEmailFieldDetails: SchemaField | undefined = React.useMemo(() => {
    return props.regFormDetailObj?.profile.find(field => field.alias === FieldAlias.WORK_EMAIL);
  }, [props.regFormDetailObj?.profile]);

  const formInitialValues = React.useMemo(() => {
    return { [EMAIL_FIELD_NAME]: workEmailFieldDetails?.value ?? "" };
  }, [workEmailFieldDetails?.value]);

  const formSubmitHandler = React.useCallback(
    async values => {
      try {
        const workEmail = values[EMAIL_FIELD_NAME];
        const isValid = await validateEmailDomain({
          params: { workEmail: workEmail, registrationFormId: props.regFormDetailObj?.id as string }
        });
        setDomainValidationResult({ email: workEmail, valid: isValid });
        if (!isValid) {
          formRef.current?.setFieldError(
            EMAIL_FIELD_NAME,
            props.regFormDetailObj?.invalidEmailDomainErrorMessage || copies.get("invalid.email")
          );
        } else {
          await updateWorkEmailAfterDomainValidation(queryClient, workEmail, props.regFormDetailObj?.id as string);
          setCurrentStep(RegistrationStep.DETAILS);
        }
      } catch (e) {}
    },
    [
      copies,
      validateEmailDomain,
      props.regFormDetailObj?.id,
      props.regFormDetailObj?.invalidEmailDomainErrorMessage,
      queryClient,
      setCurrentStep
    ]
  );

  const validationSchema = React.useMemo(() => {
    return yup.object().shape({
      [EMAIL_FIELD_NAME]: Validation.emailValidation({ copies, required: true }).test(
        "validateDomain",
        props.regFormDetailObj?.invalidEmailDomainErrorMessage || copies.get("invalid.email"),
        function (value) {
          return !(domainValidationResult?.valid === false && value === domainValidationResult.email);
        }
      )
    });
  }, [copies, domainValidationResult, props.regFormDetailObj?.invalidEmailDomainErrorMessage]);

  return { workEmailFieldDetails, formInitialValues, formSubmitHandler, validationSchema, formRef };
}
